import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import TrialCountdown from '../components/TrialCountdown';
import AIAssistant from '../components/AIAssistant';
import { 
  MessageCircle, 
  Clock, 
  Trash2, 
  Square, 
  Users, 
  Sparkles, 
  Heart, 
  Brain, 
  Star, 
  Zap, 
  ArrowRight,
  Play,
  Crown,
  Shield,
  Activity
} from 'lucide-react';
import api from '../utils/api';
import { getSpecialtyColor } from '../utils/forceRefresh';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const Home = () => {
  const { user } = useAuth();
  const { subscription, hasAccessToTherapist, loading: subscriptionLoading, isOnTrial } = useSubscription();
  const navigate = useNavigate();
  const [therapists, setTherapists] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('=== HOME COMPONENT USER EFFECT DEBUG ===');
    console.log('User in Home component:', user);
    console.log('User ID:', user?._id);
    console.log('User email:', user?.email);
    
    if (user) {
      console.log('User available, fetching data...');
      fetchData();
    } else {
      console.log('No user available yet...');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      console.log('=== fetchData called ===');
      console.log('Loading state:', loading);
      
      // Check if we have authentication
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No authentication token found, skipping API calls');
        setLoading(false);
        return;
      }
      
      console.log('Starting API calls...');
      
      const therapistsRes = await api.get('/therapists');
      
      console.log('Therapists response:', therapistsRes);
      console.log('Therapists data:', therapistsRes.data);
      setTherapists(therapistsRes.data.therapists || therapistsRes.data);
      
      // Fetch recent sessions
      const sessionsRes = await api.get('/chat/recent-sessions?limit=10');
      console.log('Sessions response:', sessionsRes);
      setRecentSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      console.error('Error details:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.response?.data);
      
      // If it's an authentication error, don't keep retrying
      if (error.response?.status === 401) {
        console.log('Authentication error detected, redirecting to login');
        // Don't set loading to false here, let the auth context handle the redirect
        return;
      }
      
      // Set empty arrays on error to prevent undefined states
      setTherapists([]);
      setRecentSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTherapistSelect = (therapistId) => {
    console.log('=== HANDLE THERAPIST SELECT DEBUG ===');
    console.log('Therapist ID:', therapistId);
    console.log('User:', user);
    console.log('Subscription:', subscription);
    console.log('Subscription loading:', subscriptionLoading);
    console.log('Has access to therapist function:', hasAccessToTherapist);
    
    // Wait for both user and subscription to be loaded
    if (!user || !subscription || subscriptionLoading) {
      console.log('User or subscription not loaded yet, waiting...');
      return;
    }
    
    if (!subscription) {
      console.log('No subscription, navigating to subscription page');
      navigate('/app/subscription');
      return;
    }
    
    const hasAccess = hasAccessToTherapist(therapistId);
    console.log('Has access to therapist:', hasAccess);
    
    if (!hasAccess) {
      console.log('No access to therapist, showing alert');
      alert('You need an Advanced Plan to access this therapist. Please upgrade your subscription.');
      return;
    }
    
    console.log('Has access, navigating to chat');
    navigate(`/app/chat/${therapistId}`);
  };

  const handleSessionContinue = (session) => {
    // Navigate to the chat page with the therapist ID and session ID
    navigate(`/app/chat/${session.therapistId}?sessionId=${session.sessionId}`);
  };

  const handleDeleteSession = async (sessionId, event) => {
    event.stopPropagation(); // Prevent triggering the session card click
    
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/chat/session/${sessionId}`);
      
      // Remove the session from the local state
      setRecentSessions(prevSessions => 
        prevSessions.filter(session => session.sessionId !== sessionId)
      );
      
      console.log('Session deleted successfully');
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete session. Please try again.');
    }
  };

  const refreshSessions = async () => {
    try {
      const sessionsRes = await api.get('/chat/recent-sessions?limit=10');
      setRecentSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Failed to refresh sessions:', error);
    }
  };

  const handleEndSession = async (sessionId, event) => {
    event.stopPropagation(); // Prevent triggering the session card click
    
    if (!window.confirm('Are you sure you want to end this session? This will mark the session as completed.')) {
      return;
    }
    
    try {
      await api.put(`/chat/end/${sessionId}`);
      
      // Refresh the sessions list to get updated data
      await refreshSessions();
      
      console.log('Session ended successfully');
    } catch (error) {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Home...</h3>
          <p className="text-gray-600">Preparing your therapy experience</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Trial Countdown */}
      <TrialCountdown />
      
      {/* Hero Welcome Section */}
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl border border-blue-100 p-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl mb-6 shadow-lg">
          <SabiwabeeLogo className="w-10 h-10" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welcome back, <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{user?.firstName} {user?.lastName}</span>!
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
          Choose a therapist to start your session or continue a previous conversation. Your mental health journey continues here.
        </p>
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span>AI Therapists</span>
          </div>
          <div className="flex items-center space-x-2">
            <Brain className="w-4 h-4 text-blue-500" />
            <span>Specialized Care</span>
          </div>
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Safe & Private</span>
          </div>
        </div>
      </div>

      {/* Plan Status */}
      {(subscription || isOnTrial()) && (
        <div className="flex justify-center">
          <div className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 px-6 py-4 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg">
                <Crown className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-gray-900">
                {isOnTrial() ? 'Trial' : (subscription?.plan === 'advanced' ? 'Advanced Plan' : 'Base Plan')}
              </span>
            </div>
            <div className="w-px h-6 bg-gray-200"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-green-600">Active</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Available Therapists */}
        <div className="bg-gradient-to-br from-white to-green-50 rounded-3xl shadow-xl border border-green-100 p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Available Therapists</h2>
          </div>
          
          <div className="space-y-4">
            {therapists.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl mb-4">
                  <Users className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">No Therapists Available</h4>
                <p className="text-gray-600">Please check back later or contact support.</p>
              </div>
            ) : (
              therapists.map((therapist) => (
                <div
                  key={therapist.id}
                  className="bg-white border-2 border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
                  onClick={() => handleTherapistSelect(therapist.id)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg">
                      {therapist.avatar ? (
                        <img 
                          src={therapist.avatar} 
                          alt={`${therapist.name} avatar`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center" 
                           style={{ display: therapist.avatar ? 'none' : 'flex' }}>
                        <span className="text-2xl font-bold text-white">
                          {therapist.name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-gray-900 text-lg">{therapist.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm ${getSpecialtyColor(therapist.specialty)}`}>
                          {therapist.specialty}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">
                        {therapist.description}
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>Specialized in {therapist.specialty}</span>
                      </div>
                    </div>
                    
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="p-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg">
                        <Play className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl border border-purple-100 p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Recent Sessions</h2>
          </div>
          
          <div className="space-y-4">
            {recentSessions.length > 0 ? (
              recentSessions.map((session) => (
                <div
                  key={session._id}
                  className="bg-white border-2 border-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group relative"
                  onClick={() => handleSessionContinue(session)}
                >
                  {/* Action buttons */}
                  <div className="absolute inset-0 flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-95 rounded-2xl">
                    {/* End session button (only for active sessions) */}
                    {(!session.status || session.status === 'active') && (
                      <button
                        onClick={(e) => handleEndSession(session.sessionId, e)}
                        className="p-3 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-xl transition-all duration-200 transform hover:scale-110"
                        title="End session"
                      >
                        <Square className="w-5 h-5" />
                      </button>
                    )}
                    
                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDeleteSession(session.sessionId, e)}
                      className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-200 transform hover:scale-110"
                      title="Delete session"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-bold text-gray-900 text-lg">{session.therapistName}</h3>
                      {session.status === 'completed' && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                          Completed
                        </span>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm ${getSpecialtyColor(session.therapistSpecialty)}`}>
                      {session.therapistSpecialty}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                    {session.lastMessage || 'No messages yet...'}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>{new Date(session.updatedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-gray-400" />
                      <span>{session.messageCount || 0} messages</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl mb-4">
                  <MessageCircle className="w-8 h-8 text-purple-500" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">No Recent Sessions</h4>
                <p className="text-gray-600 mb-4">Start a conversation with a therapist to see your sessions here.</p>
                <button
                  onClick={() => navigate('/app/chat')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2 mx-auto"
                >
                  <Zap className="w-4 h-4" />
                  <span>Start New Session</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call to Action for New Users */}
      {!subscription && (
        <div className="bg-gradient-to-br from-white to-yellow-50 rounded-3xl shadow-xl border border-yellow-100 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-3xl mb-6 shadow-lg">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to start your therapy journey?
          </h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Choose a subscription plan to access our AI therapists and begin your path to better mental health. 
            Start your transformation today.
          </p>
          <button
            onClick={() => navigate('/app/subscription')}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2 mx-auto"
          >
            <ArrowRight className="w-5 h-5" />
            <span>Choose Your Plan</span>
          </button>
        </div>
      )}

      {/* AI Assistant */}
      <AIAssistant />
    </div>
  );
};

export default Home; 